import { useEffect, useState } from "react";
import { useDragMarquee } from "@/hooks/use-drag-marquee";
import FivestarLogo from "./FivestarLogo";
import logoAdvnc from "@/assets/logos/advnc.webp";
import logoTrue from "@/assets/logos/true.webp";
import logoLegends from "@/assets/logos/legends.webp";
import logoHofstra from "@/assets/logos/hofstra.webp";
import logoEighteenTwelve from "@/assets/logos/eighteentwelve.webp";
import logoCorrigan from "@/assets/logos/corrigansports.webp";
import logoWhiteMarlin from "@/assets/logos/whitemarlinopen.webp";
import logoSmartlink from "@/assets/logos/smartlink.webp";
import logoLegacy from "@/assets/logos/legacy.webp";
import logoJw1 from "@/assets/logos/jw1.webp";
import logoImgAcademy from "@/assets/logos/IMGacademy.webp";
import logoColby from "@/assets/logos/colbycollege.webp";
import logoPll from "@/assets/logos/pll.webp";
import logoAloha from "@/assets/logos/alohalacrosse.webp";
import logoBuku from "@/assets/logos/bukuevents.webp";
import logoFca from "@/assets/logos/fca.webp";
import logoTeamIllinois from "@/assets/logos/teamillinois.webp";

/**
 * Trusted-by logo marquee plus the oversized "What Is Fivestar" introduction
 * headline. The stacked Fivestar mark sits behind the headline as a faint
 * watermark that slowly rotates with page scroll for parallax-like depth.
 */
// Final member set — each logo listed exactly once. The marquee renders two
// copies back-to-back and the keyframe translates -50%, so the strip loops
// seamlessly while every logo appears only once per scroll cycle.
type ClientLogo = { src: string; alt: string; className?: string };
const CLIENT_LOGOS: ClientLogo[] = [
  { src: logoAdvnc, alt: "ADVNC Lacrosse" },
  { src: logoTrue, alt: "True Lacrosse" },
  { src: logoLegends, alt: "Legends" },
  { src: logoHofstra, alt: "Hofstra University" },
  { src: logoEighteenTwelve, alt: "Eighteen Twelve" },
  { src: logoCorrigan, alt: "Corrigan Sports" },
  { src: logoWhiteMarlin, alt: "White Marlin Open" },
  { src: logoSmartlink, alt: "Smartlink", className: "scale-110" },
  { src: logoLegacy, alt: "Legacy" },
  { src: logoJw1, alt: "JW1" },
  { src: logoImgAcademy, alt: "IMG Academy" },
  { src: logoColby, alt: "Colby College" },
  { src: logoPll, alt: "Premier Lacrosse League" },
  { src: logoAloha, alt: "Aloha Lacrosse" },
  { src: logoBuku, alt: "Buku Events" },
  { src: logoFca, alt: "FCA" },
  { src: logoTeamIllinois, alt: "Team Illinois" },
];

/**
 * Fisher–Yates shuffle so the marquee loads in a different order each visit.
 * Returns a new array; the source CLIENT_LOGOS is left untouched.
 */
const shuffleLogos = (arr: ClientLogo[]): ClientLogo[] => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const WATERMARK_BASE_ROTATION_DEG = 0;
const WATERMARK_ROTATION_PER_PX = -0.01125;
const WATERMARK_SCALE_PER_PX = 0.0002;

const TrustedByAndIntro = () => {
  const [scrollY, setScrollY] = useState(0);
  // Shuffle once per mount so the marquee order varies between page loads.
  const [logos] = useState(() => shuffleLogos(CLIENT_LOGOS));
  const {
    containerRef: marqueeRef,
    trackRef,
    prefersReducedMotion,
    handlers: marqueeHandlers,
  } = useDragMarquee();

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        frame = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const watermarkRotation =
    WATERMARK_BASE_ROTATION_DEG + scrollY * WATERMARK_ROTATION_PER_PX;
  const watermarkScale = 1 + scrollY * WATERMARK_SCALE_PER_PX;

  return (
    <section
      id="what-is-fivestar"
      className="relative overflow-x-clip bg-white mt-24 pb-24 sm:mt-32 sm:pb-32 md:mt-40 md:pb-40 md:sticky md:top-0"
    >
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Headline with watermark */}
        <div className="relative text-center">
          <FivestarLogo
            variant="stacked"
            aria-label=""
            className="absolute left-1/2 top-1/2 w-[150vw] max-w-[2250px] text-black opacity-[0.03] pointer-events-none"
            style={{
              transform: `translate(-50%, -50%) rotate(${watermarkRotation}deg) scale(${watermarkScale})`,
            }}
          />
          <h2 className="relative text-3xl md:text-5xl font-black italic uppercase leading-[1.05] text-black">
            Trusted by over 150 organizations & 1,000 creators
          </h2>
          <p className="relative mt-6 max-w-3xl mx-auto text-base md:text-lg text-zinc-600 font-normal">
            Fivestar connects its members with professional photographers and
            videographers. Book coverage for games, events, or highlight
            sessions and get share-worthy, brand-building footage delivered in
            48 hours.
          </p>
        </div>
      </div>

      {/* Full-bleed logo marquee — auto-scrolls and is drag/swipe scrollable,
          same behavior as the testimonial strip. */}
      <div className="mt-24 md:mt-32">
        {prefersReducedMotion ? (
          <div className="overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
            <div className="flex w-max items-center px-6">
              {logos.map((logo, i) => (
                <div
                  key={i}
                  className="shrink-0 px-24 flex items-center justify-center"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className={`max-h-[3.75rem] max-w-[9rem] md:max-h-[4.5rem] md:max-w-[11rem] object-contain ${logo.className ?? ""}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            ref={marqueeRef}
            className="relative overflow-hidden select-none cursor-grab active:cursor-grabbing"
            {...marqueeHandlers}
          >
            <div
              ref={trackRef}
              className="flex w-max items-center will-change-transform"
            >
              {[...logos, ...logos].map((logo, i) => (
                <div
                  key={i}
                  className="shrink-0 px-24 flex items-center justify-center"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className={`max-h-[3.75rem] max-w-[9rem] md:max-h-[4.5rem] md:max-w-[11rem] object-contain ${logo.className ?? ""}`}
                  />
                </div>
              ))}
            </div>
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40"
              style={{
                background:
                  "linear-gradient(to right, #fff 0%, rgba(255,255,255,0) 100%)",
              }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40"
              style={{
                background:
                  "linear-gradient(to left, #fff 0%, rgba(255,255,255,0) 100%)",
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default TrustedByAndIntro;
